import { useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Menu, Divider } from '@mui/material';
import { Add, Block } from '@mui/icons-material';
import {
    usePopupState,
    bindTrigger,
    bindMenu,
} from 'material-ui-popup-state/hooks';

// Colours picked with "Custom colour" are kept per browser, one list per picker type, and
// offered after the defaults from then on.
const MAX_CUSTOM_COLORS = 8;
const customColorsKey = type => `quoting-custom-colors-${type === 'highlight' ? 'highlight' : 'text'}`;

function loadCustomColors(type) {
    try {
        const saved = JSON.parse(localStorage.getItem(customColorsKey(type)));
        return Array.isArray(saved) ? saved : [];
    } catch {
        return [];
    }
}

function saveCustomColors(type, colors) {
    try {
        localStorage.setItem(customColorsKey(type), JSON.stringify(colors));
    } catch {
        // Storage can be unavailable (private mode, quota); the colour still applies this once.
    }
}

const swatchSx = {
    width: 24,
    height: 24,
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
        transform: 'scale(1.1)',
        transition: '0.2s'
    }
};

const ColorPicker = ({ title, icon, onSelect, activeColor, onClear, type }) => {
    const popupState = usePopupState({
        variant: 'popover',
        popupId: `color-picker-${title}`,
    });
    const [customColors, setCustomColors] = useState(() => loadCustomColors(type));

    const highlightColors = [
        '#bdf3d4', // light green
        '#d1e9ff', // light blue
        '#ffdce0', // light pink
        '#e2d5f3', // light purple
        '#fff9b1', // light yellow
        '#f2f2f2', // light gray
        '#dfebf7', // pale blue
    ];

    const textColors = [
        '#000000', // black
        '#4caf50', // green
        '#03a9f4', // light blue
        '#2196f3', // blue
        '#f44336', // red
        '#ffeb3b', // yellow
        '#ff9800', // orange
        '#9c27b0', // purple

    ];

    const colors = type === 'highlight' ? highlightColors : textColors;

    function selectColor(color) {
        onSelect(color);
        popupState.close();
    }

    function updateCustomColors(next) {
        setCustomColors(next);
        saveCustomColors(type, next);
    }

    // The newest colour goes first; the oldest drops off once the list is full.
    function addCustomColor(color) {
        const normalized = color.toLowerCase();
        if (!colors.includes(normalized)) {
            updateCustomColors([normalized, ...customColors.filter(c => c !== normalized)].slice(0, MAX_CUSTOM_COLORS));
        }
        selectColor(normalized);
    }

    // React's onChange fires on every drag through the native picker, so only the native
    // `change` event -- sent once the picker is closed -- counts as choosing a colour. The
    // listener is attached once, so it reads the latest handler through a ref.
    const addCustomColorRef = useRef(addCustomColor);
    addCustomColorRef.current = addCustomColor;

    function attachCustomColorInput(input) {
        if (!input || input.dataset.listening) return;
        input.dataset.listening = 'true';
        input.addEventListener('change', e => addCustomColorRef.current(e.target.value));
    }

    const renderSwatch = (color, extraProps = {}) => (
        <Box
            key={color}
            onClick={() => selectColor(color)}
            sx={{
                ...swatchSx,
                backgroundColor: color,
                border: activeColor === color ? '2px solid #512da8' : '1px solid #e0e0e0',
            }}
            {...extraProps}
        />
    );

    return (
        <Box>
            <Tooltip title={title}>
                <IconButton size="small" {...bindTrigger(popupState)} color={activeColor ? 'primary' : 'default'}>
                    {icon}
                </IconButton>
            </Tooltip>
            <Menu {...bindMenu(popupState)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 10,
                        mt: 1,
                        boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
                        '& .MuiList-root': {
                            display: 'flex',
                            p: 1,
                            gap: 1,
                            alignItems: 'center'
                        }
                    }
                }}
            >
                {colors.map(color => renderSwatch(color))}
                {customColors.length > 0 && (
                    <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, height: 20, my: 'auto', mx: 0.5 }} />
                )}
                {customColors.map(color => (
                    <Tooltip key={color} title="Right-click to remove">
                        {renderSwatch(color, {
                            onContextMenu: e => {
                                e.preventDefault();
                                updateCustomColors(customColors.filter(c => c !== color));
                            },
                        })}
                    </Tooltip>
                ))}
                <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, height: 20, my: 'auto', mx: 0.5 }} />
                <Tooltip title="Custom colour">
                    <Box
                        component="label"
                        sx={{
                            ...swatchSx,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed #9e9e9e',
                            position: 'relative',
                        }}
                    >
                        <Add sx={{ fontSize: 16, color: '#666' }} />
                        <input
                            ref={attachCustomColorInput}
                            type="color"
                            defaultValue={activeColor || colors[0]}
                            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, border: 0, padding: 0 }}
                        />
                    </Box>
                </Tooltip>
                <Tooltip title="Default colour">
                    <Box
                        onClick={() => {
                            onClear();
                            popupState.close();
                        }}
                        sx={{
                            ...swatchSx,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e0e0e0',
                            mr: 0.5,
                        }}
                    >
                        <Block sx={{ fontSize: 16, color: '#666' }} />
                    </Box>
                </Tooltip>
            </Menu>
        </Box>
    );
};

export default ColorPicker;
